import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";

const services = [
  {
    title: "Frontend Development",
    description:
      "We build sleek, responsive UIs using React, Next.js, and Tailwind CSS. Focused on accessibility, performance, and pixel-perfect design.",
    icon: "🖥️",
  },
  {
    title: "Backend Development",
    description:
      "Robust server-side apps using Node.js, Express, MongoDB, and REST/GraphQL APIs to power your business logic securely.",
    icon: "🛠️",
  },
  {
    title: "Full-Stack Solutions",
    description:
      "From idea to deployment, we offer end-to-end full-stack development, integrating frontend, backend, and databases seamlessly.",
    icon: "🌐",
  },
  {
    title: "CMS & E-Commerce",
    description:
      "We build dynamic websites using WordPress, Strapi, Shopify, or custom headless CMS tailored to your content and store needs.",
    icon: "🛒",
  },
  {
    title: "Deployment & Optimization",
    description:
      "Optimized deployment with CI/CD, Vercel, Netlify, and AWS. SEO, performance tuning, and security best practices included.",
    icon: "🚀",
  },
];

export default function Webdevblog() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  return (
    <main className="bg-white text-gray-800">
      <Helmet>
        <title>Web Development Services | Codexa</title>
        <meta
          name="description"
          content="Explore Codexa's full-range web development services including frontend, backend, full-stack, CMS, and scalable deployments."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-indigo-600 text-white py-20 px-6 text-center mt-2">
        <motion.div
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mx-auto"
        >
          <h1 className="text-4xl md:text-5xl font-bold">Web Development Services</h1>
          <p className="mt-4 text-lg md:text-xl text-indigo-100">
            Codexa delivers scalable, modern websites and web apps designed for performance and success.
          </p>
        </motion.div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-6 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white border border-gray-200 shadow-md rounded-2xl p-6 hover:shadow-xl transition"
            >
              <div className="text-4xl mb-4">{service.icon}</div>
              <h3 className="text-xl font-semibold text-indigo-600 mb-2">{service.title}</h3>
              <p className="text-gray-700">{service.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gray-100 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto px-4"
        >
          <h2 className="text-3xl font-bold text-indigo-600 mb-4">
            Ready to Build with Codexa?
          </h2>
          <p className="text-gray-700 mb-6">
            Whether you're a startup or enterprise, we tailor our services to meet your goals.
          </p>
          <a
            href="/contact"
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-full text-lg transition shadow-lg"
          >
            Contact Us
          </a>
        </motion.div>
      </section>
    </main>
  );
}
